import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useState } from 'react';
import { UserDetailHeader } from '@/components/features/operator/user-detail-header';
import { TabNavigation } from '@/components/features/operator/TabNavigation';
import type { TabType } from '@/components/features/operator/TabNavigation';
import { TabContent } from '@/components/features/operator/TabContent';
import { Icons } from '@/components/ui/icons';
import { type UserRole } from '@/types/validators';

const UserDetailPage = () => {
  const { t } = useTranslation('operator');
  const { userId } = Route.useParams();
  const searchParams = Route.useSearch();

  // Query user details from backend
  const userDetails = useQuery(api.app.operator.operatorQueries.operatorGetUserDetails, userId ? { userId: userId as any } : 'skip');

  // Read tab from URL query parameter, default to 'personal'
  const initialTab = (searchParams as any)?.tab as TabType | undefined;

  // Build valid tabs based on user role
  const getValidTabs = (role?: UserRole): TabType[] => {
    const baseTabs: TabType[] = ['personal', 'bank', 'preferences'];
    const providerTabs: TabType[] = role === 'provider' ? ['facilities', 'services'] : [];
    const documentsTabs: TabType[] = role === 'substitute' ? ['documents'] : [];
    const verificationTabs: TabType[] = role === 'substitute' ? ['verification'] : [];
    const endTabs: TabType[] = ['statistics'];
    return [...baseTabs, ...providerTabs, ...documentsTabs, ...verificationTabs, ...endTabs];
  };

  const validTabs = getValidTabs(userDetails?.user?.role);
  const defaultTab: TabType = initialTab && validTabs.includes(initialTab) ? initialTab : 'personal';

  const [activeTab, setActiveTab] = useState<TabType>(defaultTab);

  // Loading state
  if (userDetails === undefined) {
    return (
      <div className='flex items-center justify-center min-h-screen' role='status' aria-live='polite' aria-label={t('userDetail.loading')}>
        <div className='text-center space-y-4'>
          <Icons.Loader className='h-8 w-8 animate-spin text-muted-foreground mx-auto' />
          <p className='text-sm text-muted-foreground'>{t('userDetail.loading')}</p>
          <p className='text-xs text-muted-foreground'>{t('userDetail.userId', { id: userId })}</p>
          <p className='text-xs font-mono text-muted-foreground'>{t('userDetail.checkConsoleLower')}</p>
        </div>
        <span className='sr-only'>{t('userDetail.loading')}</span>
      </div>
    );
  }

  // Bug #9 Fix: Improved error handling with specific error types
  if (!userDetails || !userDetails.user) {
    // Determine error type for better user feedback
    const isNetworkError = userDetails === undefined;
    const isNotFound = userDetails === null || !userDetails?.user;

    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center space-y-4 p-6 border border-destructive rounded-lg bg-destructive/5 max-w-md'>
          <Icons.AlertTriangle className='h-8 w-8 text-destructive mx-auto' />
          <p className='text-sm text-destructive font-bold'>{isNotFound ? t('userDetail.userNotFound') : t('userDetail.error')}</p>
          <p className='text-xs text-muted-foreground'>{t('userDetail.userId', { id: userId })}</p>

          {isNetworkError && (
            <div className='text-xs text-muted-foreground space-y-2'>
              <p>{t('userDetail.network.unreachable')}</p>
              <p>{t('userDetail.network.checkConnection')}</p>
            </div>
          )}

          {isNotFound && (
            <div className='text-xs text-muted-foreground space-y-2'>
              <p>{t('userDetail.notFound.explanation')}</p>
              <p>{t('userDetail.notFound.maybeRemoved')}</p>
            </div>
          )}

          <div className='flex flex-col gap-2 pt-2'>
            <button onClick={() => window.location.reload()} className='text-xs underline text-primary hover:no-underline'>
              {t('userDetail.reloadPage')}
            </button>
            <button onClick={() => window.history.back()} className='text-xs underline text-muted-foreground hover:no-underline'>
              {t('userDetail.backToOverview')}
            </button>
          </div>

          <p className='text-xs font-mono text-muted-foreground pt-2'>{t('userDetail.checkConsole')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='container mx-auto p-4 sm:p-6 lg:p-8 space-y-6'>
      {/* Header with user info and completeness */}
      <UserDetailHeader user={userDetails.user} profile={userDetails.profile} completeness={userDetails.completeness} />

      {/* Tab navigation */}
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} userRole={userDetails.user.role} />

      {/* Tab content area */}
      <TabContent activeTab={activeTab} userDetails={userDetails} />
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/users/$userId')({
  component: UserDetailPage,
});
